import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import { useLanguageStore, replaceTemplateWithElements } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import {
  getTeamHomeList,
  isTeamHomeAccessSuspendedError,
} from '$/domains/workFlowV3/teamCanvasService';
import type {
  TeamHomeProjectItem,
  V3CanvasProjectItem,
} from '$/domains/workFlowV3/type';
import { getCanvasProjectListV3 } from '$/domains/workFlowV3/workFlowV3Service';
import classNames from 'classnames';
import { throttle } from 'lodash';
import { useEventListener } from 'ahooks';
import { setHeaderConfig, useCommonStore } from '@/store/useCommonStore';
import useTeamStore from '@/store/useTeamStore';
import { navigate } from '@/common/agents';
import { trackRoboneoAssetUploadClick } from '@/components/AssetModalContent/tracking';
import TeamAccessSuspendedOverlay from '@/components/TeamAccessSuspendedOverlay';
import { getHomeCaseV3, getHomeCategories } from '$/domains/caseV3/caseV3Service';
import { SHARE_TRY_MINE_KEY } from '@/pages/--roboneo/share/index.page';
import type {
  V3CanvasHomeCaseItem,
  V3CanvasHomeCategoryInfo,
  V3CanvasHomeCategoryItem,
} from '$/domains/caseV3/type';
import useInputComposer, {
  DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS,
} from '$/hooks/useInputComposer';
import useCreateRoom from '$/hooks/useCreateRoom';
import HotPromptCaseList from './HotPromptCaseList';
import HotPromptCategoryTabs from './HotPromptCategoryTabs';
import FeaturedCaseSection from './FeaturedCaseSection';
import HomeToolBar from './HomeToolBar';
import HomeInputPanel from './HomeInputPanel';
import DotMotionBackground from './DotMotionBackground';
// import CollapseInputChat from '../CollapseInputChat';
import styles from './index.module.scss';

const SHOW_CASE_SCROLL_TOP = 400;
const SCROLL_CONTAINER_CLASS_NAME = 'homeScrollContainer';
const HOME_SCROLL_POSITION_KEY = 'roboneo_home_scroll_position';
const HOME_SCROLL_RESTORE_MAX_RETRY = 20;
const HOME_SCROLL_RESTORE_RETRY_INTERVAL = 100;

type HomeProjectItem = V3CanvasProjectItem | TeamHomeProjectItem;

/**
 * 将首页输入区接口的分类树拆成扁平分类列表和扁平案例列表。
 */
function flattenHomeInputCategories(categoryList: V3CanvasHomeCategoryItem[]) {
  const flatCategoryList: V3CanvasHomeCategoryInfo[] = [];
  const flatCaseList: V3CanvasHomeCaseItem[] = [];
  categoryList.forEach((category) => {
    const { cases = [], ...categoryInfo } = category;
    flatCategoryList.push({
      ...categoryInfo,
    });

    cases.forEach((item) => {
      flatCaseList.push({
        ...item,
        category_id: item.category_id ?? category.category_id,
      });
    });
  });

  return { categoryList: flatCategoryList, caseList: flatCaseList };
}

/**
 * 从案例 graph_json 中读取用户输入，避免单个异常案例影响首页输入框回填。
 */
function getCaseGraphUserInput(caseInfo: V3CanvasHomeCaseItem) {
  try {
    const graphData = JSON.parse(caseInfo.graph_json || '{}');
    return graphData?.userInput || null;
  } catch (err) {
    console.error('解析案例 graph_json 失败:', err);
    return null;
  }
}

/**
 * 记录跳转详情页前的首页滚动位置，供返回首页后恢复。
 */
function saveHomeScrollPosition() {
  try {
    sessionStorage.setItem(HOME_SCROLL_POSITION_KEY, String(window.scrollY || 0));
  } catch (error) {
    console.error('保存首页滚动位置失败:', error);
  }
}

/**
 * 消费已保存的首页滚动位置，避免后续普通进入首页时误恢复。
 */
function consumeHomeScrollPosition() {
  try {
    const raw = sessionStorage.getItem(HOME_SCROLL_POSITION_KEY);
    sessionStorage.removeItem(HOME_SCROLL_POSITION_KEY);
    const scrollTop = Number(raw);
    return Number.isFinite(scrollTop) && scrollTop > 0 ? scrollTop : null;
  } catch (error) {
    console.error('读取首页滚动位置失败:', error);
    return null;
  }
}

/**
 * 渲染 RoboNeo 首页主体和首屏输入区。
 */
function BaseHome() {
  // const navbarExpand = useCommonStore((state) => state.navbarExpand);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useOfficialStore((state) => state.toLogin);
  const initialized = useCommonStore((state) => state.initialized);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const teamOid = useTeamStore((state) => state.teamInfo?.oid);
  const isTeam = Boolean(teamOid);
  const homeProjectCreateLocation = isTeam ? 'team_add' : 'home_personal_space';
  // 没在使用，使用 window 滚动代替。为了解决右上角滚动条被虚化的问题
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const initializedComposerRef = useRef(false);
  const pendingHomeScrollTopRef = useRef<number | null>(null);
  const restoreHomeScrollTimerRef = useRef<number>();
  const [recentProjects, setRecentProjects] = useState<HomeProjectItem[]>([]);
  const [promptGroups, setPromptGroups] = useState<V3CanvasHomeCategoryInfo[]>([]);
  const [selectedPromptGroup, setSelectedPromptGroup] = useState<V3CanvasHomeCategoryInfo | null>(null);
  // const [selectedHotPrompt, setSelectedHotPrompt] = useState<SelectedHotPromptContext | null>(null);
  const [hotPrompts, setHotPrompts] = useState<V3CanvasHomeCaseItem[]>([]);
  const [featuredCasesLoaded, setFeaturedCasesLoaded] = useState(false);
  const [teamAccessSuspended, setTeamAccessSuspended] = useState(false);

  const { createRoom } = useCreateRoom({
    onLoginSuccessed: async () => {
      await initUserInfo();
    },
  });

  /**
   * 打开资产库前处理登录态，未登录时阻止打开弹层。
   */
  const beforeOpenAssetLibrary = useCallback(async () => {
    if (!isUserLoggedIn) {
      await toLogin().then(() => { initUserInfo(); }).catch(() => {});
      return false;
    }
    return true;
  }, [initUserInfo, isUserLoggedIn, toLogin]);

  /**
   * 资产库入口点击埋点。
   */
  const handleAssetLibraryOpen = useCallback(() => {
    trackRoboneoAssetUploadClick('home_page');
  }, []);

  const inputComposer = useInputComposer({
    surface: 'home',
    config: {
      ...DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS,
      enableSelectMode: true,
      enableThinkingMode: true,
      enableSkillSelect: true,
      forceShowModelSelect: true,
      trackLocation: 'home_page',
    },
    beforeOpenAssetLibrary,
    onAssetLibraryOpen: handleAssetLibraryOpen,
  });

  /**
   * 初始化首页输入框底部工具、模型和 Skill 数据。
   */
  useEffect(() => {
    if (!initialized || initializedComposerRef.current) return;
    initializedComposerRef.current = true;
    inputComposer.actions.initInputButtonConfig();
    inputComposer.actions.initModelConfig();
    inputComposer.actions.initSkillData();
  }, [initialized, inputComposer.actions]);

  /**
   * 消费分享页/Agent 广场写入的回填数据，新版首页输入框需要直接展示 Agent mention。
   */
  useEffect(() => {
    const raw = sessionStorage.getItem(SHARE_TRY_MINE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(SHARE_TRY_MINE_KEY);
    try {
      inputComposer.actions.setPopupDataContent(JSON.parse(raw));
    } catch (error) {
      console.error('解析分享回填数据失败:', error);
    }
  }, [inputComposer.actions]);

  /** 身份初始化后按个人或团队空间刷新项目，并在切换身份时清空旧空间数据。 */
  useEffect(() => {
    let canceled = false;
    setRecentProjects([]);
    setTeamAccessSuspended(false);
    if (!initialized || !isUserLoggedIn) return undefined;

    /** 首页仅取两个可打开项目，团队目录中的文件夹不作为项目卡片展示。 */
    const loadRecentProjects = async () => {
      try {
        if (teamOid) {
          const response = await getTeamHomeList({
            folder_id: 0,
            cursor: 0,
            count: 20,
          });
          if (canceled) return;
          setRecentProjects(
            response.list
              .filter((item): item is TeamHomeProjectItem => item.type === 'project')
              .slice(0, 2),
          );
          return;
        }

        const response = await getCanvasProjectListV3({
          tab: 'agent-team',
          cursor: 0,
          count: 2,
        });
        if (canceled) return;
        setRecentProjects(
          response.list
            .filter((project) => Number(project.kind ?? 1) !== 2)
            .slice(0, 2),
        );
      } catch (error) {
        if (canceled) return;
        setRecentProjects([]);
        if (teamOid && isTeamHomeAccessSuspendedError(error)) {
          setTeamAccessSuspended(true);
        }
      }
    };

    loadRecentProjects();

    return () => {
      canceled = true;
    };
  }, [initialized, isUserLoggedIn, teamOid]);

  /**
   * 获取首页推荐案例分类和列表
   */
  useEffect(() => {
    getHomeCategories({
      type: 'workflow', preview_count: 20, scene: 'home_input', with_graph: 1,
    }).then((res) => {
      const { categoryList, caseList } = flattenHomeInputCategories(res.list || []);
      setPromptGroups(categoryList);
      setHotPrompts(caseList);
    }).catch(() => {});
  }, []);

  /**
   * 异步案例区撑开页面高度后，重试恢复跳转前的首页滚动位置。
   */
  const restoreHomeScrollPosition = useCallback(() => {
    const savedTop = pendingHomeScrollTopRef.current ?? consumeHomeScrollPosition();
    if (savedTop === null) return;
    pendingHomeScrollTopRef.current = savedTop;

    if (restoreHomeScrollTimerRef.current) {
      window.clearTimeout(restoreHomeScrollTimerRef.current);
    }

    let retryCount = 0;
    /**
     * 页面异步内容可能还没撑开高度，逐帧/短延迟重试直到滚动成功。
     */
    const restore = () => {
      window.scrollTo({ top: savedTop, behavior: 'auto' });
      const restored = Math.abs(window.scrollY - savedTop) <= 2;
      const reachedMaxRetry = retryCount >= HOME_SCROLL_RESTORE_MAX_RETRY;
      if (restored || reachedMaxRetry) {
        pendingHomeScrollTopRef.current = null;
        return;
      }

      retryCount += 1;
      restoreHomeScrollTimerRef.current = window.setTimeout(restore, HOME_SCROLL_RESTORE_RETRY_INTERVAL);
    };

    window.requestAnimationFrame(restore);
  }, []);

  useEffect(() => {
    restoreHomeScrollPosition();
  }, [featuredCasesLoaded, hotPrompts.length, restoreHomeScrollPosition]);

  useEffect(() => {
    return () => {
      if (restoreHomeScrollTimerRef.current) {
        window.clearTimeout(restoreHomeScrollTimerRef.current);
      }
    };
  }, []);

  /**
   * 根据页面滚动位置更新案例区吸顶和 Header 背景。
   */
  const updateByScroll = () => {
    const show = window.scrollY > SHOW_CASE_SCROLL_TOP;
    setHeaderConfig({ showHeaderBackgroundColor: show, showBlurBackground: !show });
  };

  useEventListener('scroll', throttle(updateByScroll, 100));
  useEffect(() => updateByScroll(), []);

  /**
   * 切换首页输入框下方的热门 prompt 分类，同时清空已选案例。
   */
  const handlePromptGroupChange = (category: V3CanvasHomeCategoryInfo | null) => {
    setSelectedPromptGroup(category);
    // setSelectedHotPrompt(null);
  };

  /**
   * 做同款跳转房间页面
   *
   */

  const handleTryButtonClick = (item: V3CanvasHomeCaseItem) => {
    getHomeCaseV3({
      case_id: item.case_id,
    }).then((res) => {
      const detail = res.case;
      if (!detail) return;
      const data = getCaseGraphUserInput(detail);
      createRoom({
        caseId: item.case_id.toString(),
        withCanvasData: true,
        isAutoSubmit: false,
        prompt: data?.content || undefined,
        skillList: data?.skillList || undefined,
        markElementList: data?.markElementList || undefined,
        agentInfoList: data?.agentList || undefined,
        trackLocation: homeProjectCreateLocation,
        autoPrefixUnreferencedResources: false,
      });
    }).catch((err) => {
      console.error('获取当前案例详情失败:', err);
    });
  };

  /**
   * 记录当前选中的热门 prompt，提交时用于组装 sop 等上下文。
   */
  const handleHotPromptClick = (item: V3CanvasHomeCaseItem | null) => {
    if (!item) return;
    const userInput = getCaseGraphUserInput(item);
    if (userInput) {
      inputComposer.actions.setPopupDataContent({
        text: userInput.content || '',
        markElementList: userInput.markElementList || [],
        agentInfoList: userInput.agentList || [],
        skillList: userInput.skillList || undefined,
      });
    }
  };

  /**
   * 打开案例详情前保存首页位置，让关闭详情页回退后停留在原点击位置。
   */
  const handleCaseCardClick = useCallback((item: V3CanvasHomeCaseItem) => {
    saveHomeScrollPosition();
    navigate({
      mode: 'router',
      pathname: '/--roboneo/case_detail',
      query: { id: item.case_id, category: item.category_id },
    });
  }, []);

  /**
   * 标记首页推荐案例区加载完成，用于触发滚动位置二次恢复。
   */
  const handleFeaturedCaseLoaded = useCallback(() => {
    setFeaturedCasesLoaded(true);
  }, []);

  return (
    <div
      ref={scrollContainerRef}
      className={classNames(styles.homeContainer, SCROLL_CONTAINER_CLASS_NAME)}
    >
      <div className={classNames(styles.homeBox, 'homeBox')}>
        <DotMotionBackground />

        <div className={styles.heroSection}>
          <div className={styles.title}>
            {/* 今天想一起创作点什么呢？ */}
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_create_today_question)}
          </div>
          <div className={styles.inputBox}>
            <div className={styles.home_input_box}>
              <HotPromptCategoryTabs
                categoryList={promptGroups}
                selectedCategory={selectedPromptGroup}
                onCategoryChange={handlePromptGroupChange}
              />
              <div className={styles.input_content}>
                <HomeInputPanel
                  inputPlaceholder={languageStore.smartcatTranslationText((v) => v.home_page_input_field_text)}
                  className={styles.home_input}
                  inputComposer={inputComposer}
                  selectedPromptGroup={selectedPromptGroup}
                  createTrackLocation={homeProjectCreateLocation}
                />

              </div>
              <HotPromptCaseList
                className={styles.inspirationCaseCarousel}
                homeInputCaseList={hotPrompts}
                selectedCategory={selectedPromptGroup}
                onPromptClick={handleHotPromptClick}
              />
            </div>
          </div>
        </div>
        <div className={styles.contentSection}>
          <HomeToolBar recentProjects={recentProjects} />
          <FeaturedCaseSection
            onCardClick={handleCaseCardClick}
            onTryButtonClick={(item) => {
              handleTryButtonClick(item);
            }}
            onLoaded={handleFeaturedCaseLoaded}
          />
        </div>

        <div className={styles.footerInfo}>
          {
            isOversea ? (
              <div className={styles.text_box}>
                {replaceTemplateWithElements('roboneo_web_connect_overseas', {
                  license: (
                    <a
                      className={styles.link}
                      target="_blank"
                      href="https://beian.miit.gov.cn/"
                      rel="noreferrer"
                    >
                      闽B2-20040192-92
                    </a>
                  ),
                  support_email: (
                    <a className={styles.link} href="mailto:support@roboneo.com">
                      support@roboneo.com
                    </a>
                  ),
                  api_email: (
                    <a className={styles.link} href="mailto:aigc@meitu.com">
                      aigc@meitu.com
                    </a>
                  ),
                })}
              </div>
            ) : (
              <>
                <div className={styles.text_box}>
                  {languageStore.smartcatTranslationText((v) => v.roboneo_web_license_1)}
                </div>
                <div className={styles.text_box}>
                  {replaceTemplateWithElements('roboneo_web_connect', {
                    license: (
                      <a
                        className={styles.link}
                        target="_blank"
                        href="https://beian.miit.gov.cn/"
                        rel="noreferrer"
                      >
                        闽B2-20040192-92
                      </a>
                    ),
                    support_email: (
                      <a className={styles.link} href="mailto:support@roboneo.com">
                        support@roboneo.com
                      </a>
                    ),
                    api_email: (
                      <a className={styles.link} href="mailto:aigc@meitu.com">
                        aigc@meitu.com
                      </a>
                    ),
                  })}
                </div>
              </>
            )
          }

        </div>
      </div>

      {teamAccessSuspended && <TeamAccessSuspendedOverlay />}
    </div>
  );
}
export default BaseHome;
